/**
 * TTS-LATENCY-V7 + REFINO-pos-a05eb3a — detector frontend de queries
 * que devem usar o path vanilla (POST /assistant/message) em vez de stream.
 *
 * POR QUÊ:
 *   O backend tem 1 caminho de short-circuit (zero LLM) que vive APENAS
 *   na rota vanilla:
 *     - datacard short-circuit (services/datacard_templates.SHORT_CIRCUIT_TYPES)
 *   Datacard responde em ~100-500ms via template puro; manter aqui é
 *   o ganho V5/V6.
 *
 * MUDANÇA (a05eb3a, 2026-06-02):
 *   Antes, briefing (resumo dia/semana/mês) também só vivia em vanilla
 *   (humanizer bloqueante 2-4s). Esses patterns estavam listados aqui
 *   pra preservar latência. Pós-a05eb3a, o /stream agora tem
 *   render_summary_stream — TTFT ~500ms vs full 2-4s do vanilla. Resumos
 *   PRECISAM ir pelo stream. BRIEFING_PATTERNS removidos desta árvore.
 *
 *   Resultado da auditoria (docs/_insumos_pos_a05eb3a.md):
 *     8 turnos humanizados pós-deploy → só 2 streamaram (25%). Os 6
 *     bloqueantes foram pegos pelos BRIEFING_PATTERNS aqui. Removendo
 *     esses patterns, expectativa: ~100% streaming para resumo +
 *     7 query handlers.
 *
 *   Voz usa o mesmo `chat.sendMessage` (NectarDashboard:313) → o gating
 *   é compartilhado. Removendo briefing aqui resolve voz junto.
 *
 * MIRROR LEVE:
 *   Não duplicamos a árvore inteira do backend — só keywords de datacard.
 *   Falso positivo (vanilla quando podia stream) custa o ganho de stream.
 *   Falso negativo (stream quando podia datacard) regride latência mas
 *   funcionalidade idêntica — backend responde via LLM e emite o
 *   datacard via hook (linha 965 do routes/assistant.py).
 *
 * MANUTENÇÃO:
 *   Quando adicionar keyword nova em SHORT_CIRCUIT_TYPES no backend,
 *   espelhe aqui se for relevante pra UX de voz. Cobertura em
 *   src/utils/__tests__/shortCircuitDetector.test.js.
 */
"use strict";

// Keywords que o backend trata via datacard short-circuit (subset de
// SHORT_CIRCUIT_TYPES — expense_summary, balance_overview, upcoming_payments,
// task_overview, investment_overview, subscription_summary).
//
// Lower-case. Match por substring case-insensitive (não word-boundary)
// — mesma semântica do detect_datacard_type backend.
const DATACARD_SHORT_CIRCUIT_KEYWORDS = [
  // expense_summary
  "quanto gastei", "gastos do mes", "gastos do mês", "meus gastos",
  "quanto gastou", "gastei esse mes", "gastei esse mês",
  "total gasto", "despesas do mes", "despesas do mês",
  "minhas despesas", "qual é a minha despesa", "mostrar minhas despesas",
  "minhas despesa", "quanto eu gastei",
  // balance_overview
  "meu saldo", "saldo total", "quanto tenho",
  "como ta o saldo", "como tá o saldo", "saldo das contas",
  "quanto tem na conta", "patrimonio", "patrimônio",
  "qual o meu saldo", "qual é o meu saldo", "saldo do mes",
  "saldo do mês",
  // upcoming_payments
  "pagar hoje", "tenho a pagar", "proximos vencimentos",
  "próximos vencimentos", "contas a pagar",
  "vencimentos da semana", "o que vence",
  "tenho algo pra pagar", "tenho algo para pagar",
  // task_overview
  "minhas tarefas", "minhas tarefas pendentes",
  "o que tenho pra fazer", "o que tenho para fazer",
  "tarefas pendentes", "o que to atrasado", "o que tô atrasado",
  "tarefas dessa semana", "tarefas da semana",
  "quantas tarefas eu tenho", "liste minhas tarefas",
  "tarefas atrasadas",
  // investment_overview
  "meus investimentos", "como ta minha carteira",
  "como tá minha carteira", "quanto investi", "rendimento",
  "carteira de investimentos", "patrimonio investido",
  "patrimônio investido",
  // subscription_summary
  "minhas assinaturas", "quanto pago em assinaturas",
  "subscriptions", "quanto sai em assinaturas",
  "proximas cobrancas", "próximas cobranças",
  "quantas assinaturas eu tenho", "quantas assinaturas",
  "tenho quantas assinaturas",
];

// REFINO-pos-a05eb3a (2026-06-02): BRIEFING_PATTERNS removidos. Resumos
// agora streamam pelo /assistant/message/stream (render_summary_stream).
// Datacard continua vivendo só em vanilla — keywords abaixo.

/**
 * True se a mensagem deve preferir vanilla (rota /assistant/message)
 * em vez de stream — porque o backend tem short-circuit pra ela.
 *
 * Só datacard short-circuit (template puro, ~200ms). Resumos e os 7
 * query handlers agora vão pelo /stream (humanizer streamado, TTFT ~500ms).
 *
 * @param {string} text - texto digitado pelo user
 * @returns {boolean}
 */
function shouldPreferVanilla(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim().toLowerCase();
  if (!t) return false;
  // Datacard keywords é substring match
  for (const kw of DATACARD_SHORT_CIRCUIT_KEYWORDS) {
    if (t.includes(kw)) return true;
  }
  return false;
}

module.exports = {
  shouldPreferVanilla,
  // Exposed pra testes
  DATACARD_SHORT_CIRCUIT_KEYWORDS,
};
